'use client';

import { useToast } from '@common/hooks';
import { CustomError, isEnumValue, type ServerResponse } from '@core/utils';
import * as Sentry from '@sentry/nextjs';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { pick } from 'es-toolkit';
import { toSnakeCaseKeys } from 'es-toolkit/object';
import { decodeJwt } from 'jose';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { AuthClient, AuthQueryOptions, MEMBER_JOIN_STATUS, onAuthExpired, UnauthApi } from '@/api';
import { ROUTES, STORAGE_KEY } from '@/constants';
import { useAccessToken } from '@/hooks/useAccessToken';
import { useLogout } from '@/hooks/useLogout';
import type { MyInfo } from '@/stores/AuthStore';
import { useAuthStore } from '@/stores/AuthStore';
import { GTM_EVENT_TYPE, trackGtmEvent } from '@/utils/analytics/gtm';
interface AuthProviderProps {
  children: React.ReactNode;
}
export const AuthProvider = ({
  children
}: AuthProviderProps) => {
  const [setMyInfo, isLogin, isDisabledAccessControl] = useAuthStore(useShallow(state => [state.setMyInfo, state.isLogin, state.isDisabledAccessControl]));
  const {
    accessToken,
    refreshToken,
    setAccessToken,
    setRefreshToken
  } = useAccessToken();
  AuthClient.setConfig({
    auth: accessToken || undefined
  });
  const {
    logout
  } = useLogout();
  const pathname = usePathname();
  const {
    toastError,
    toast
  } = useToast();
  const isRefreshing = useRef(false);
  const refreshAndSaveTokens = useCallback(async () => {
    if (!refreshToken || isRefreshing.current) return false;
    isRefreshing.current = true;
    try {
      const res = await UnauthApi['get:/wello/v2/allows/member-oauth/sns/refresh-token']({
        query: {
          refreshToken
        }
      });
      const newAccessToken = res.data?.context?.accessToken;
      const newRefreshToken = res.data?.context?.refreshToken;
      if (newAccessToken && newRefreshToken) {
        setAccessToken(newAccessToken);
        setRefreshToken(newRefreshToken);
        AuthClient.setConfig({
          auth: newAccessToken
        });
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      isRefreshing.current = false;
    }
  }, [refreshToken, setAccessToken, setRefreshToken]);

  // 401 갱신 실패 시 로그아웃 핸들러 등록
  useEffect(() => {
    onAuthExpired(() => {
      //* forInit: 서버 로그아웃 호출을 건너뛴다. 이미 만료된 토큰으로 호출하면 다시 401 →
      //* 만료 핸들러가 재호출되는 순환이 생긴다.
      logout({
        forInit: true,
        toastOptions: {
          message: '로그인이 만료되었어요. 다시 로그인해 주세요.',
          type: 'fail'
        }
      });

      //* 토큰 핸드오프 중(cross-domain)에는 이동하지 않는다 — 아직 토큰이 전달되는 구간이라
      //* 여기서 하드 이동하면 KB 앱 스킴 복귀가 끊긴다. 로그인 화면 자체도 이동 대상이 아니다.
      const NAVIGATION_EXCLUDED_PATHNAMES = [ROUTES.CROSS_DOMAIN_AUTH.pathname, ROUTES.CROSS_DOMAIN_CALLBACK.pathname, ROUTES.LOGIN.pathname];
      if (NAVIGATION_EXCLUDED_PATHNAMES.includes(location.pathname)) return;

      //* forInit 경로는 화면 전환을 하지 않고(KB 서브도메인도 동일) 토큰만 비운다.
      //* 그대로 두면 가입 폼 등 로그인 전용 화면에 빈 상태로 갇히므로 재로그인 경로로 보낸다.
      location.replace(ROUTES.LOGIN.withSearchParams({
        searchParams: {
          redirect: location.pathname + location.search
        }
      }));
    });
  }, [logout]);

  // accessToken이 없고 refreshToken만 있을 때 갱신
  useEffect(() => {
    // 로그아웃 중이면 토큰 갱신 방지
    if (isDisabledAccessControl) return;
    if (accessToken || !refreshToken) return;
    const refresh = async () => {
      const success = await refreshAndSaveTokens();
      if (!success) logout({
        forInit: true
      });
    };
    refresh();
  }, [accessToken, refreshToken, logout, refreshAndSaveTokens, isDisabledAccessControl]);

  //* 사전 갱신: 만료 10분 전에 미리 토큰 갱신
  useEffect(() => {
    if (isDisabledAccessControl) return;
    if (!accessToken || !refreshToken) return;
    try {
      const decoded = decodeJwt(accessToken);
      const expiresAt = dayjs.unix(decoded.exp ?? 0);
      const refreshAt = expiresAt.subtract(10, 'minute');
      const delay = refreshAt.diff(dayjs());
      if (delay <= 0) return;
      const timer = setTimeout(() => {
        refreshAndSaveTokens();
      }, delay);
      return () => clearTimeout(timer);
    } catch {
      // JWT 디코딩 실패
    }
  }, [accessToken, refreshToken, refreshAndSaveTokens, isDisabledAccessControl]);

  // 토큰 없으면 로그아웃 (cross domain 제외)
  useEffect(() => {
    if (!accessToken && !refreshToken && pathname && ![ROUTES.CROSS_DOMAIN_AUTH.pathname, ROUTES.CROSS_DOMAIN_CALLBACK.pathname].includes(pathname)) {
      logout({
        forInit: true
      });
    }
  }, [accessToken, refreshToken, pathname, logout]);
  const myInfoQuery = useQuery({
    ...AuthQueryOptions['get:/wello/v2/auth/member/profile'](),
    enabled: !!accessToken,
    select: data => {
      const {
        context
      } = data;
      const {
        memberStatusCd,
        saveStatusCd
      } = context ?? {};
      let memberJoinStatus = saveStatusCd === 'WELLO_MEMBER_FILTER_SAVE_STATUS_WEAKNESS' ? MEMBER_JOIN_STATUS.COMPLETE : saveStatusCd;
      if (memberJoinStatus === MEMBER_JOIN_STATUS.COMPLETE && memberStatusCd === MEMBER_JOIN_STATUS.NO_PHONE) {
        memberJoinStatus = MEMBER_JOIN_STATUS.NO_PHONE;
      }
      if (!isEnumValue(MEMBER_JOIN_STATUS, memberJoinStatus)) throw new Error('save_status_cd가 유효하지 않은 값입니다.');
      if (!context) {
        throw new CustomError({
          return_message: 'context가 필요합니다.'
        });
      }
      return {
        ...pick(context, ['email', 'age', 'gender', 'region', 'codeGender', 'marketingMessageAppPushUpdateAt', 'marketingMessageEmailUpdateAt', 'marketingMessageMobileUpdateAt']),
        nickName: context?.nickName,
        id: context?.memberOAuthId,
        isVerification: context?.verificationCompletedYN,
        profileImage: context?.profileImage,
        birthDate: context?.birthDate,
        memberCode: context?.memberCode,
        subRegion: context?.subRegion,
        codeRegion: context?.codeRegion,
        codeSubRegion: context?.codeSubRegion,
        cellPhone: context?.cellPhone,
        memberJoinStatus,
        memberOAuth: context?.memberOAuthId,
        marketingMessageReceiverUpdateAt: context?.marketingMessageReceiverUpdateAt,
        sensitiveInfoYN: context?.sensitiveInfoYN,
        notificationOptions: pick(context, ['bestContentsReceiveYN', 'hometownNewsReceiveYN', 'communityYN', 'likeCommentYN', 'policyApplyMessageReceiveYN', 'marketingMessageReceiveYN', 'popularPolicyReceiveYN', 'replyCommentYN', 'reportCommentYN', 'suggestPolicyReceiveYN', 'marketingMessageMobileReceiveYN', 'marketingMessageAppPushReceiveYN', 'marketingMessageEmailReceiveYN', 'welloPushConsentYN', 'homeLovePushConsentYN', 'localMarketPushConsentYN'])
      } satisfies MyInfo;
    }
  });
  useEffect(() => {
    const logoutToast = sessionStorage.getItem(STORAGE_KEY.LOGOUT_TOAST);
    if (!logoutToast) return;
    toast(JSON.parse(logoutToast));
    sessionStorage.removeItem(STORAGE_KEY.LOGOUT_TOAST);
  }, [toast]);
  useEffect(() => {
    if (myInfoQuery.isError) {
      logout({
        redirect: ROUTES.HOME.pathname,
        toastOptions: {
          type: 'fail',
          message: (myInfoQuery.error as ServerResponse).return_message ?? ''
        }
      });
      return;
    }
    if (!myInfoQuery.isFetched || myInfoQuery.isLoading) return;
    if (!myInfoQuery.data) {
      logout({
        toastOptions: {
          type: 'fail',
          message: '내 정보를 불러오는데 실패했습니다.'
        }
      });
      return;
    }
    setMyInfo(myInfoQuery.data);
    if (myInfoQuery.data.id) {
      Sentry.setUser({
        id: String(myInfoQuery.data.id)
      });
    }
  }, [logout, myInfoQuery.data, myInfoQuery.error, myInfoQuery.isError, myInfoQuery.isFetched, myInfoQuery.isLoading, setMyInfo, toastError]);
  const userPropertyQuery = useQuery({
    ...AuthQueryOptions['get:/wello/v2/auth/member/user-property'](),
    enabled: !!accessToken,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    select: ({
      context: context_0
    }) => context_0
  });
  useEffect(() => {
    if (userPropertyQuery.isFetched && userPropertyQuery.data) {
      trackGtmEvent(GTM_EVENT_TYPE.USER_PROPERTY, isLogin ? toSnakeCaseKeys(userPropertyQuery.data) : {
        is_login: 'N'
      });
    }
  }, [userPropertyQuery.data, userPropertyQuery.isFetched, isLogin]);
  return <>{children}</>;
};